import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActivityCatalog, useBoard, useRegister } from '@lemontaigu/staff/react';
import type { ActivityQualification, BoardActivity } from '@lemontaigu/staff';
import {
  contractRange,
  fmtDateShort,
  groupByTeam,
  teamLabel,
  type AdminEmployee,
} from './logic.js';

// « Équipe » (RH group since 2026-08-06 — it is maintained via Contrats; operator-only): the current team at a glance —
// only employees under contract today, grouped by team.
//
// EMPLOYMENT facts stay read-only here: identity, PINs and contract periods are
// edited in RH → Contrats/Registre. What IS editable is the one OPERATIONAL fact
// that belongs to a person rather than to an activity — their « activité
// principale ».
//
// That split is the nav split (2026-07-29): Opérations runs the place, RH
// administers the employment. Before it, this file said « read-only on purpose,
// all editing lives in Registre », which was right when Équipe and Contrats sat
// in the same group and there was nothing operational to edit here.
//
// PRINCIPALE, NOT QUALIFICATION. Qualification (« can do ») is edited in the
// Activités matrix, which is activity-first — the right shape for « who can cover
// this ». Principale (« normally does ») is person-first, which is why it lives
// here. Deliberately NOT both in both places: one relation with two editors is
// one relation with two answers.

export function Equipe() {
  const register = useRegister();
  const board = useBoard();
  const catalog = useActivityCatalog();
  const [rows, setRows] = useState<AdminEmployee[] | null>(null);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [quals, setQuals] = useState<ActivityQualification[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [employees, acts, qualifications] = await Promise.all([
        register.listEmployees(),
        board.activities(),
        board.qualifications(),
      ]);
      setRows(employees);
      setActivities(acts);
      setQuals(qualifications);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [register, board]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = (rows ?? []).filter((e) => e.isCurrent);
  const groups = groupByTeam(current);
  const byActivityId = useMemo(() => new Map(activities.map((a) => [a.id, a])), [activities]);

  /** This person's qualifications, in board order, with their « principale » flag. */
  const activitiesOf = (employeeId: string) =>
    quals
      .filter((q) => q.employeeId === employeeId)
      .map((q) => ({ activity: byActivityId.get(q.activityId), isMain: q.isMain === true }))
      .filter((x): x is { activity: BoardActivity; isMain: boolean } => x.activity !== undefined)
      .sort((a, b) => a.activity.sort - b.activity.sort || a.activity.label.localeCompare(b.activity.label));

  const toggleMain = async (activityId: number, employeeId: string, isMain: boolean) => {
    setBusy(true);
    setErr('');
    try {
      await catalog.setMain(activityId, employeeId, isMain);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="equipe">
      <div className="equipe-head">
        <h2 className="section-title">Équipe — en poste</h2>
        <span className="equipe-hint">
          {current.length} en poste · fiches et contrats dans <strong>RH → Contrats</strong> · cochez ici
          l’activité <strong>principale</strong> de chacun (ce qu’il ou elle fait normalement)
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {rows === null && <p className="staff-empty">Chargement…</p>}
      {rows !== null && current.length === 0 && !err && (
        <p className="staff-empty">Personne sous contrat aujourd’hui.</p>
      )}

      {groups.map((g) => (
        <section key={g.team || 'none'} className={`team-block team-${g.team || 'none'}`}>
          <header className="team-head">
            <span className="team-dot" />
            <h3>{g.team ? teamLabel(g.team) : 'Sans équipe'}</h3>
            <span className="team-count">{g.members.length}</span>
          </header>
          <div className="team-grid">
            {g.members.map((e) => (
              <article key={e.id} className="team-card">
                <div className="team-card-name">
                  {e.firstName} {e.lastName}
                  {e.nickname && <span className="reg-nickname">« {e.nickname} »</span>}
                  {e.isSenior && <span className="emp-badge">senior</span>}
                </div>
                <div className="team-card-meta">
                  <span>ID Paie {e.employeeId}</span>
                  <span>{e.phone}</span>
                </div>
                <div className="team-card-meta">
                  <span>
                    {e.contractStartDate
                      ? contractRange({ startDate: e.contractStartDate, endDate: e.contractEndDate })
                      : `depuis ${fmtDateShort(e.firstStartDate)}`}
                  </span>
                </div>
                <div className="team-card-mains">
                  {activitiesOf(e.id).length === 0 ? (
                    <span className="team-card-muted">aucune qualification — voir l’onglet Activités</span>
                  ) : (
                    activitiesOf(e.id).map(({ activity, isMain }) => (
                      <label key={activity.id} className={isMain ? 'main-chip is-main' : 'main-chip'}>
                        <input
                          type="checkbox"
                          checked={isMain}
                          disabled={busy}
                          onChange={(ev) => void toggleMain(activity.id, e.id, ev.target.checked)}
                        />
                        <span>{activity.label}</span>
                      </label>
                    ))
                  )}
                </div>
                <div className="team-card-dots">
                  <span className={e.hasPin ? 'emp-ok' : 'emp-warn'}>
                    {e.hasPin ? '● PIN' : '○ PIN à définir'}
                  </span>
                  <span className={e.isLinked ? 'emp-ok' : 'team-card-muted'}>
                    {e.isLinked ? '● compte lié' : '○ jamais connecté·e'}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
